// ===========================================
// Class Links API (web links as class resources, 2026-09-07)
// ===========================================

import { apiClient } from '../services/api';
import { FileVisibility } from './resources.api';

export type LinkProvider = 'YOUTUBE' | 'VIMEO' | 'WEB';

export interface ClassLink {
  id: string;
  schoolId: string;
  lessonId: string | null;
  programLevelId: string | null;
  url: string;
  title: string;
  description: string | null;
  provider: LinkProvider;
  providerId: string | null;
  visibility: FileVisibility;
  sortOrder: number;
  addedById: string;
  createdAt: string;
  updatedAt: string;
  Lesson?: { id: string; name: string } | null;
  ProgramLevel?: { id: string; name: string } | null;
  User?: { id: string; firstName: string; lastName: string } | null;
}

export interface ClassLinksQuery {
  lessonId?: string;
  programLevelId?: string;
  /** Narrow to one student's enrolled classes (the parent page's child tabs). */
  studentId?: string;
}

export interface CreateClassLinkInput {
  lessonId?: string;
  programLevelId?: string;
  url: string;
  title: string;
  description?: string;
  visibility?: FileVisibility;
}

export interface UpdateClassLinkInput {
  url?: string;
  title?: string;
  description?: string | null;
  visibility?: FileVisibility;
  sortOrder?: number;
}

export const classLinksApi = {
  list: (filters?: ClassLinksQuery): Promise<ClassLink[]> =>
    apiClient
      .get<{ status: string; data: { links: ClassLink[]; total: number } }>('/class-links', { params: filters })
      .then((res) => res.data.links),

  create: (data: CreateClassLinkInput): Promise<ClassLink> =>
    apiClient.post<{ status: string; data: ClassLink }>('/class-links', data).then((res) => res.data),

  update: (id: string, data: UpdateClassLinkInput): Promise<ClassLink> =>
    apiClient.patch<{ status: string; data: ClassLink }>(`/class-links/${id}`, data).then((res) => res.data),

  remove: (id: string): Promise<void> =>
    apiClient.delete(`/class-links/${id}`).then(() => undefined),
};

/** Host label for the card chip: "YouTube", "Vimeo", or the site's hostname. */
export function getLinkHostLabel(link: Pick<ClassLink, 'provider' | 'url'>): string {
  if (link.provider === 'YOUTUBE') return 'YouTube';
  if (link.provider === 'VIMEO') return 'Vimeo';
  try {
    return new URL(link.url).hostname.replace(/^www\./, '');
  } catch {
    return 'Link';
  }
}

/** YouTube thumbnail for a link with a known video id; null otherwise. */
export function getLinkThumbnail(link: Pick<ClassLink, 'provider' | 'providerId'>): string | null {
  if (link.provider === 'YOUTUBE' && link.providerId) {
    return `https://i.ytimg.com/vi/${encodeURIComponent(link.providerId)}/hqdefault.jpg`;
  }
  return null;
}
